import type { OnboardingStepKey } from '../../config/onboardingRegistry'
import { ONBOARDING_STEPS } from '../../config/onboardingRegistry'
import { onboardingPreselectCountForStep } from './pickOnboardingOffers'
import { isOnboardingRoiDismissed } from './onboardingRoiCardState'

/** Kroki przewodnika podstawowego — Allegro + opisomat (bonus +30). */
export const ONBOARDING_GUIDE_SEQUENCE: OnboardingStepKey[] = [
  'create_account',
  'connect_allegro',
  'descriptions_flow',
]

/**
 * Steps the user walks through in the guide UI.
 * Excludes endowed `create_account` (auto-complete, never navigable).
 */
export const ONBOARDING_GUIDE_ACTIONABLE_SEQUENCE: OnboardingStepKey[] =
  ONBOARDING_GUIDE_SEQUENCE.filter((k) => k !== 'create_account')

/** Opcjonalne kroki checklisty po bonusie (nie blokują +30). */
export const ONBOARDING_OPTIONAL_STEP_KEYS: OnboardingStepKey[] = [
  'titles_flow',
  'grafikomat_flow',
  'parameters_flow',
]

/** Moduły odblokowane dopiero po ukończeniu przewodnika podstawowego (Challenges). */
export const ONBOARDING_POST_GUIDE_STEP_KEYS: OnboardingStepKey[] = [
  'create_bundle',
  'copy_offer',
]

/** Visible checklist rows — required Allegro + opisomat only. */
export const ONBOARDING_CHECKLIST_STEPS = ONBOARDING_STEPS.filter(
  (s) =>
    !s.endowed &&
    ONBOARDING_GUIDE_ACTIONABLE_SEQUENCE.includes(s.key),
)

/** Optional checklist rows after core bonus. */
export const ONBOARDING_OPTIONAL_CHECKLIST_STEPS = ONBOARDING_STEPS.filter((s) =>
  ONBOARDING_OPTIONAL_STEP_KEYS.includes(s.key),
)

export type OnboardingFlowStepKey =
  | 'titles_flow'
  | 'descriptions_flow'
  | 'grafikomat_flow'
  | 'parameters_flow'

export const ONBOARDING_ROI_FLOW_STEPS: OnboardingFlowStepKey[] = [
  'descriptions_flow',
  'titles_flow',
  'grafikomat_flow',
  'parameters_flow',
]

/** Szacowany czas ręcznej pracy na 1 ofertę (minuty). */
export const ONBOARDING_FLOW_MINUTES_PER_OFFER: Record<OnboardingFlowStepKey, number> = {
  titles_flow: 7,
  descriptions_flow: 25,
  grafikomat_flow: 20,
  parameters_flow: 15,
}

export function isPostGuideStep(stepKey: OnboardingStepKey): boolean {
  return ONBOARDING_POST_GUIDE_STEP_KEYS.includes(stepKey)
}

export function isOptionalGuideStep(stepKey: OnboardingStepKey): boolean {
  return ONBOARDING_OPTIONAL_STEP_KEYS.includes(stepKey)
}

export function isOnboardingGuideCoreComplete(
  completedKeys: Set<OnboardingStepKey>,
): boolean {
  return ONBOARDING_GUIDE_ACTIONABLE_SEQUENCE.every((k) => completedKeys.has(k))
}

export function areOptionalGuideStepsComplete(
  completedKeys: Set<OnboardingStepKey>,
): boolean {
  return ONBOARDING_OPTIONAL_STEP_KEYS.every((k) => completedKeys.has(k))
}

export function guideStepTotal(): number {
  return ONBOARDING_GUIDE_ACTIONABLE_SEQUENCE.length
}

export function guideCompletedCount(completedKeys: Set<OnboardingStepKey>): number {
  return ONBOARDING_GUIDE_ACTIONABLE_SEQUENCE.filter((k) => completedKeys.has(k)).length
}

/** 1-based position of the active/next step in the actionable guide (0 if unknown). */
export function guideCurrentStepNumber(stepKey: OnboardingStepKey | null | undefined): number {
  if (!stepKey) return 0
  const idx = ONBOARDING_GUIDE_ACTIONABLE_SEQUENCE.indexOf(stepKey)
  return idx >= 0 ? idx + 1 : 0
}

/**
 * Shared X/Y for dashboard + module banners.
 * Uses current-step position (not completed count) so both surfaces match.
 */
export function guideProgressDisplay(currentStepKey: OnboardingStepKey | null | undefined): {
  current: number
  total: number
} {
  return {
    current: guideCurrentStepNumber(currentStepKey),
    total: guideStepTotal(),
  }
}

/** Przewodnik ukończony — albo flaga API, albo wszystkie kroki actionable w statusie. */
export function isOnboardingGuideFinished(
  status: { all_completed?: boolean } | null | undefined,
  completedKeys: Set<OnboardingStepKey>,
): boolean {
  return Boolean(status?.all_completed) || isOnboardingGuideCoreComplete(completedKeys)
}

/** Bonus still unpaid — never tease +N after onboarding_reward_granted_at. */
export function isOnboardingCompletionRewardAtStake(
  status?: {
    reward_granted?: boolean
    points_at_stake?: number
  } | null,
): boolean {
  if (!status) return false
  if (status.reward_granted) return false
  if (typeof status.points_at_stake === 'number') return status.points_at_stake > 0
  return true
}

const RECENT_REWARD_GRANT_MS = 15 * 60 * 1000

/** True when the completion bonus was granted in this session (not months ago). */
export function isRecentOnboardingRewardGrant(
  rewardGrantedAt: string | null | undefined,
): boolean {
  if (!rewardGrantedAt) return false
  const ms = Date.parse(rewardGrantedAt)
  if (Number.isNaN(ms)) return false
  return Date.now() - ms < RECENT_REWARD_GRANT_MS
}

export function estimateGuideFlowTimeSavedMinutes(
  completedKeys: Set<OnboardingStepKey>,
): number {
  let total = 0
  for (const key of ONBOARDING_ROI_FLOW_STEPS) {
    if (completedKeys.has(key)) {
      const offerCount = onboardingPreselectCountForStep(key)
      total += offerCount * ONBOARDING_FLOW_MINUTES_PER_OFFER[key]
    }
  }
  return total
}

export function formatGuideTimeSavedMinutes(minutes: number, locale: string): string {
  if (minutes <= 0) return '—'
  if (minutes < 60) {
    return new Intl.NumberFormat(locale, { style: 'decimal' }).format(minutes) + ' min'
  }
  const hours = Math.floor(minutes / 60)
  const rest = minutes % 60
  if (rest === 0) return `${hours} h`
  return `${hours} h ${rest} min`
}

/** Jedna karta ROI — ostatni ukończony flow, który nie został odrzucony. */
export function activeOnboardingRoiStep(
  completedKeys: Set<OnboardingStepKey>,
): OnboardingFlowStepKey | null {
  for (let i = ONBOARDING_ROI_FLOW_STEPS.length - 1; i >= 0; i--) {
    const key = ONBOARDING_ROI_FLOW_STEPS[i]
    if (completedKeys.has(key) && !isOnboardingRoiDismissed(key)) {
      return key
    }
  }
  return null
}

export function isFirstFlowRoiStep(stepKey: OnboardingFlowStepKey): boolean {
  return stepKey === 'descriptions_flow'
}
